import styled from '@emotion/styled';
import P1 from '@base/P1/P1';
import P2 from '@base/P2/P2';
import { useState, useEffect } from 'react';
import { Spacings } from '@styles/index';
import SessionDetailsDrawer from '@cont/OneToOneLiveMentorship/SessionDetails/SessionDetailsDrawer';
import Drawer from '@base/Drawer/DrawerWithFooter';
import Button from '@base/Button/Button';
import Dialog from '@comp/Dialog/Dialog';
import Image from '@base/Image';
import pushRoute from '@utils/pushRoute';
import CircularLoader from '@base/CircularLoader/CircularLoader';
import Dynamic from '@base/Dynamic/Dynamic';
import { useRouter } from 'next/router';
import useSessionStore from '@stores/live-mentorship/sessionStore';
import useGoalInfo from '@api/hooks/goal/useGoalInfo';
import useSessionDetails from '@api/hooks/oneToOneLiveMentorship/useSessionDetails';
import useLmpStore from '@stores/planner/lmpStore';
import { getLMPCancelledSession } from '@stores/selectors/planner';
import useLMPSessionsInfo from '@api/hooks/oneToOneLiveMentorship/useLMPSessionsInfo';
import { LMPSessionStates } from '@constants/index';

const SessionCancellation = Dynamic(() =>
  import('@cont/OneToOneLiveMentorship/SessionDetails/SessionCancellation')
);
const RescheduleSession = Dynamic(() =>
  import('@cont/OneToOneLiveMentorship/SessionDetails/RescheduleSession')
);

const LoaderDiv = styled.div`
  grid-column: span 10;
  margin: auto;
  display: flex;
  align-items: center;
`;

const Loader = styled(CircularLoader)``;

const TextDiv = styled.div`
  display: flex;
  flex-direction: column;
`;

const SessionButtonDrawer = styled.div`
  background: var(--color-base-1);
  box-shadow: 0px -16px 32px var(--color-shadow);
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  padding-left: ${Spacings.SPACING_12B};
  padding-right: 116px;
  padding-top: ${Spacings.SPACING_4B};
  padding-bottom: ${Spacings.SPACING_4B};
`;

const SessionCancelButton = styled(Button)`
  padding: ${Spacings.SPACING_3B} ${Spacings.SPACING_4B};
  width: auto;
`;

const SessionRefundConfirmText = styled.p`
  color: var(--color-text-secondary);
  font-size: 12px;
  margin: auto;
`;

const ErrorDiv = styled.div`
  display: flex;
  align-items: center;
`;

const InfoDiv = styled.div`
  display: flex;
  align-items: center;
`;

const InfoImage = styled(Image)``;

const InfoText = styled(P2)`
  margin-left: ${Spacings.SPACING_2B};
  max-width: 279px;
  color: var(--color-text-secondary);
`;

const ErrorImage = styled(Image)``;

const ErrorText = styled(P2)`
  color: var(--color-i-red);
  margin-left: ${Spacings.SPACING_2B};
`;

const Footer = ({ footerData, errorMsg }) => {
  return (
    <>
      {footerData?.button && (
        <SessionButtonDrawer>
          <TextDiv>
            {footerData.text ? (
              <SessionRefundConfirmText>
                {footerData.text}
              </SessionRefundConfirmText>
            ) : null}
            {footerData.infoText ? (
              <InfoDiv>
                <InfoImage
                  src="oneToOneLiveMentoring/infoCircleoutline.svg"
                  width={Spacings.SPACING_6B}
                  height={Spacings.SPACING_6B}
                  alt="info"
                />
                <InfoText>{footerData.infoText}</InfoText>
              </InfoDiv>
            ) : null}
            <ErrorDiv>
              {errorMsg ? (
                <ErrorImage
                  src="oneToOneLiveMentoring/errorExclamation.svg"
                  width={Spacings.SPACING_6B}
                  height={Spacings.SPACING_6B}
                  alt="error"
                />
              ) : null}
              <ErrorText> {errorMsg}</ErrorText>
            </ErrorDiv>
          </TextDiv>
          <SessionCancelButton
            label={footerData.button.label}
            variant={footerData.button.variant}
            color={footerData.button.theme}
            onClick={footerData.button.onClick}
          />
        </SessionButtonDrawer>
      )}
    </>
  );
};

const DialogImageWrapper = styled.div`
  padding-top: ${Spacings.SPACING_10B};
  text-align: center;
  @media (max-width: 47.95em) {
    width: 100%;
    padding: 16px;
  }
`;

const DialogDataSmallHeading = styled(P1)`
  font-weight: normal;
  color: var(--color-text-secondary);
  margin-top: ${Spacings.SPACING_2B};
`;

const SessionDetails = ({ goalUID }) => {
  const { route } = useRouter();

  const {
    sessionDrawerUID: sessionUID,
    setSessionDrawerUID,
    setBookLMPSessionData
  } = useSessionStore();
  const { goalInfo = {} } = useGoalInfo(goalUID);
  const { isCancelled: isSessionCancelled } = useLmpStore(
    getLMPCancelledSession(goalUID, sessionUID)
  );

  const [sesssionCancellation, setSessionCancellation] = useState(null);
  const [errorMsg, setErrorMsg] = useState(null);
  const [footerData, setFooterData] = useState(null);

  const [sessionCreditsRefunded, setSessionCreditsRefunded] = useState(null);
  const [isRescheduleWelcomeSession, setRescheduleWelcomeSession] =
    useState(false);
  const [prevStep, setPrevStep] = useState(null);

  const [rescheduleFlowStep, setRescheduleFlowStep] =
    useState('BOOK_TIME_SLOT');

  const { mutate: fetchLmpUpcomingSessions } = useLMPSessionsInfo(
    goalUID,
    LMPSessionStates.upcoming
  );
  const { clearCancelPlannerLmpSession } = useLmpStore();

  const acceptOnClick = () => {
    setSessionDrawerUID(null);
    setSessionCancellation(null);
    clearCancelPlannerLmpSession(goalUID, sessionUID);
    if (route.includes('planner')) {
      pushRoute(
        '/goal/[goalSlug]/[goalUID]',
        `/goal/${goalInfo.slug}/${goalInfo.uid}`,
        { shallow: false }
      );
    } else {
      pushRoute(
        '/goal/[goalSlug]/[goalUID]/one-to-one-livementorship',
        `/goal/${goalInfo.slug}/${goalInfo.uid}/one-to-one-livementorship`
      );
    }
  };
  const dialogMsg = sessionCreditsRefunded ? (
    <DialogDataSmallHeading>
      We’ve refunded {sessionCreditsRefunded} session{' '}
      {sessionCreditsRefunded === 1 ? ' credit' : ' credits'} back to you
    </DialogDataSmallHeading>
  ) : null;

  const onClose = () => {
    setSessionDrawerUID(null);
    setSessionCancellation(null);
    setErrorMsg(null);
    setFooterData(null);
    setRescheduleWelcomeSession(false);
    setPrevStep(null);
    setBookLMPSessionData({}, true);
  };

  const onPressBack = () => {
    if (prevStep === 'BOOK_TIME_SLOT') {
      setRescheduleWelcomeSession(true);
      setSessionCancellation(null);
      setErrorMsg(null);
      setPrevStep(null);
    } else {
      setRescheduleWelcomeSession(false);
      setSessionCancellation(null);
      setErrorMsg(null);
    }
  };

  const { sessionDetails: sessionDetailsStore, mutate: refetchSessionDetails } =
    useSessionDetails(sessionUID);
  const { isWelcomeSession } = sessionDetailsStore;

  useEffect(() => {
    refetchSessionDetails();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionUID, isSessionCancelled]);

  const welcomeMsg = (
    <DialogDataSmallHeading>
      We will notify once your mentor is available to re-book your welcome
      session
    </DialogDataSmallHeading>
  );

  const onSessionRescheduled = () => {
    if (route.includes('one-to-one-livementorship')) {
      fetchLmpUpcomingSessions();
    }
  };

  useEffect(() => {
    if (isSessionCancelled) {
      setTimeout(() => {
        setSessionDrawerUID(null);
        setSessionCancellation(null);
        setFooterData(null);
        if (route.includes('planner')) {
          pushRoute(
            '/goal/[goalSlug]/[goalUID]',
            `/goal/${goalInfo.slug}/${goalInfo.uid}`,
            { shallow: false }
          );
        } else {
          fetchLmpUpcomingSessions();
          pushRoute(
            '/goal/[goalSlug]/[goalUID]/one-to-one-livementorship',
            `/goal/${goalInfo.slug}/${goalInfo.uid}/one-to-one-livementorship`,
            { shallow: false }
          );
        }
      }, 3000);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSessionCancelled]);

  return (
    <>
      <Drawer
        drawerState={!!sessionUID && !isSessionCancelled}
        onClose={onClose}
        showBackButton={
          sesssionCancellation ||
          (isRescheduleWelcomeSession &&
            rescheduleFlowStep === 'BOOK_TIME_SLOT')
        }
        hideCloseButton={
          sesssionCancellation ||
          (isRescheduleWelcomeSession &&
            rescheduleFlowStep === 'BOOK_TIME_SLOT')
        }
        onPressBack={onPressBack}
        footer={<Footer footerData={footerData} errorMsg={errorMsg} />}
      >
        {!sessionDetailsStore.uid ? (
          <LoaderDiv>
            <Loader color="var(--color-i-blue)" />
          </LoaderDiv>
        ) : null}
        {!sesssionCancellation &&
        sessionDetailsStore.uid &&
        !isRescheduleWelcomeSession ? (
          <SessionDetailsDrawer
            setSessionCancellation={setSessionCancellation}
            setFooterData={setFooterData}
            goalUID={goalUID}
            setRescheduleWelcomeSession={setRescheduleWelcomeSession}
            setRescheduleFlowStep={setRescheduleFlowStep}
          />
        ) : null}
        {isRescheduleWelcomeSession && (
          <RescheduleSession
            goalUID={goalUID}
            setErrorMsg={setErrorMsg}
            setFooterData={setFooterData}
            setSessionCancellation={setSessionCancellation}
            setRescheduleWelcomeSession={setRescheduleWelcomeSession}
            rescheduleFlowStep={rescheduleFlowStep}
            setRescheduleFlowStep={setRescheduleFlowStep}
            onClose={onClose}
            setPrevStep={setPrevStep}
            onSessionRescheduled={onSessionRescheduled}
          />
        )}
        {sesssionCancellation &&
        sessionDetailsStore.uid &&
        !isRescheduleWelcomeSession ? (
          <SessionCancellation
            goalUID={goalUID}
            setSessionCreditsRefunded={setSessionCreditsRefunded}
            sesssionCancellation={sesssionCancellation}
            setFooterData={setFooterData}
            setErrorMsg={setErrorMsg}
          />
        ) : null}
      </Drawer>
      <Dialog
        dialogState={isSessionCancelled}
        acceptOnClick={acceptOnClick}
        acceptLabel="Got it"
        acceptLabelVariant="hollow"
        showButtons
        heading={`Your ${
          isWelcomeSession ? 'welcome ' : ''
        }session is cancelled`}
        message={isWelcomeSession ? welcomeMsg : dialogMsg}
        aboveHeading={
          <DialogImageWrapper>
            <Image
              src="session_cancelled_dialog.svg"
              alt="cancelled-session"
              width={120}
              height={120}
            />
          </DialogImageWrapper>
        }
      />
    </>
  );
};

export default SessionDetails;
